import React, { useState, useMemo, useCallback } from 'react';
import { Table, Input, Button, Space, Typography, Card, Tooltip } from 'antd';
import { ReloadOutlined, ExportOutlined, FilterOutlined } from '@ant-design/icons';
import type { TableProps, ColumnType } from 'antd/es/table';
import type { SorterResult, FilterValue, TableCurrentDataSource } from 'antd/es/table/interface';
import { colors, spacing, shadows, createFlexBetween } from '../../styles/theme';
import LoadingSpinner from './LoadingSpinner';

const { Search } = Input;
const { Title } = Typography;

interface DataTableProps<T = any> extends Omit<TableProps<T>, 'columns' | 'dataSource' | 'title'> {
  /** 테이블 제목 */
  title?: string;
  /** 컬럼 정의 */
  columns: ColumnType<T>[];
  /** 테이블 데이터 */
  data?: T[];
  /** 로딩 상태 */
  loading?: boolean;
  /** 에러 상태 */
  error?: string | null;
  /** 검색 기능 활성화 */
  searchable?: boolean;
  /** 새로고침 기능 활성화 */
  refreshable?: boolean;
  /** 내보내기 기능 활성화 */
  exportable?: boolean;
  /** 필터링 기능 활성화 */
  filterable?: boolean;
  /** 페이지네이션 설정 */
  paginationConfig?: {
    pageSize?: number;
    showSizeChanger?: boolean;
    showQuickJumper?: boolean;
    showTotal?: (total: number, range: [number, number]) => string;
  };
  /** 검색 플레이스홀더 */
  searchPlaceholder?: string;
  /** 새로고침 핸들러 */
  onRefresh?: () => void;
  /** 내보내기 핸들러 */
  onExport?: () => void;
  /** 검색 핸들러 */
  onSearch?: (value: string) => void;
  /** 테이블 변경 핸들러 (정렬, 필터링 등) */
  onTableChange?: (
    pagination: any,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<T> | SorterResult<T>[],
    extra: TableCurrentDataSource<T>
  ) => void;
  /** 빈 상태 메시지 */
  emptyMessage?: string;
  /** 추가 액션 버튼들 */
  extraActions?: React.ReactNode;
  /** 테이블 상단 추가 콘텐츠 */
  headerExtra?: React.ReactNode;
}

/**
 * 통합 데이터 테이블 컴포넌트
 * 
 * 검색, 정렬, 필터링, 페이지네이션 등의 기능을 내장한 재사용 가능한 테이블 컴포넌트입니다.
 * 일관된 UI/UX와 접근성을 제공하며, 성능 최적화가 적용되어 있습니다.
 */
const DataTable = <T extends Record<string, any>>({
  title,
  columns,
  data = [],
  loading = false,
  error = null,
  searchable = true,
  refreshable = true,
  exportable = false,
  filterable = false,
  paginationConfig = {},
  searchPlaceholder = '검색...',
  onRefresh,
  onExport,
  onSearch,
  onTableChange,
  emptyMessage = '데이터가 없습니다.',
  extraActions,
  headerExtra,
  ...tableProps
}: DataTableProps<T>) => {
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(paginationConfig.pageSize || 10);

  // 검색된 데이터 메모이제이션
  const filteredData = useMemo(() => {
    if (!searchValue.trim()) return data;
    
    return data.filter(item => {
      return Object.values(item).some(value => 
        String(value).toLowerCase().includes(searchValue.toLowerCase())
      );
    });
  }, [data, searchValue]);

  // 검색 핸들러
  const handleSearch = useCallback((value: string) => {
    setSearchValue(value);
    setCurrentPage(1); // 검색 시 첫 페이지로 이동
    onSearch?.(value);
  }, [onSearch]);

  // 새로고침 핸들러
  const handleRefresh = useCallback(() => {
    setSearchValue('');
    setCurrentPage(1);
    onRefresh?.();
  }, [onRefresh]);

  // 페이지네이션 설정
  const paginationSettings = useMemo(() => ({
    current: currentPage,
    pageSize: pageSize,
    total: filteredData.length,
    showSizeChanger: paginationConfig.showSizeChanger ?? true,
    showQuickJumper: paginationConfig.showQuickJumper ?? true,
    showTotal: paginationConfig.showTotal || ((total, range) => 
      `${range[0]}-${range[1]} / 총 ${total}개`
    ),
    onChange: (page: number, size: number) => {
      setCurrentPage(page);
      setPageSize(size);
    },
    onShowSizeChange: (_current: number, size: number) => {
      setCurrentPage(1);
      setPageSize(size);
    },
    ...paginationConfig,
  }), [currentPage, pageSize, filteredData.length, paginationConfig]);

  // 에러 상태 렌더링
  if (error) {
    return (
      <Card>
        <div style={{ 
          textAlign: 'center', 
          padding: spacing[8],
          color: colors.error[500] 
        }}>
          <Typography.Text type="danger">
            {error}
          </Typography.Text>
          {refreshable && (
            <div style={{ marginTop: spacing[4] }}>
              <Button 
                type="primary" 
                icon={<ReloadOutlined />} 
                onClick={handleRefresh}
              >
                다시 시도
              </Button>
            </div>
          )}
        </div>
      </Card>
    );
  }

  return (
    <Card
      style={{
        boxShadow: shadows.card,
        borderRadius: 8,
      }}
      styles={{
        body: { padding: 0 }
      }}
    >
      {/* 헤더 영역 */}
      <div style={{
        padding: spacing[4],
        borderBottom: `1px solid ${colors.border.secondary}`,
        backgroundColor: colors.background.tertiary,
      }}>
        <div style={createFlexBetween()}>
          <div style={{ display: 'flex', alignItems: 'center', gap: spacing[4] }}>
            {title && (
              <Title level={4} style={{ margin: 0 }}>
                {title}
              </Title>
            )}
            {headerExtra}
          </div>

          <Space size="middle">
            {searchable && (
              <Search
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onSearch={handleSearch}
                style={{ minWidth: 250 }}
                allowClear
              />
            )}

            {extraActions}

            {filterable && (
              <Tooltip title="필터">
                <Button 
                  icon={<FilterOutlined />} 
                  type="text"
                />
              </Tooltip>
            )}

            {exportable && (
              <Tooltip title="내보내기">
                <Button 
                  icon={<ExportOutlined />} 
                  type="text"
                  onClick={onExport}
                />
              </Tooltip>
            )}

            {refreshable && (
              <Tooltip title="새로고침">
                <Button 
                  icon={<ReloadOutlined />} 
                  type="text"
                  onClick={handleRefresh}
                  loading={loading}
                />
              </Tooltip>
            )}
          </Space>
        </div>
      </div>

      {/* 테이블 영역 */}
      <div style={{ padding: spacing[4] }}>
        {loading ? (
          <LoadingSpinner 
            message="데이터를 불러오는 중..."
            minHeight={300}
          />
        ) : (
          <Table<T>
            columns={columns}
            dataSource={filteredData}
            pagination={paginationSettings}
            onChange={onTableChange}
            locale={{
              emptyText: emptyMessage,
              filterTitle: '필터',
              filterConfirm: '확인',
              filterReset: '초기화',
              selectAll: '모두 선택',
              selectInvert: '선택 반전',
              sortTitle: '정렬',
            }}
            scroll={{ x: 'max-content' }}
            size="middle"
            {...tableProps}
            style={{
              backgroundColor: colors.background.primary,
              ...tableProps.style,
            }}
          />
        )}
      </div>
    </Card>
  );
};

export default React.memo(DataTable) as typeof DataTable;